/**
 * VariantB, Sprint 2.5a A.0/B/S1 (Heidi-pattern hero rev).
 *
 * Centred SmartIntentBar with a cycling category row above. The
 * cycling row is the "Scribe / Evidence / Comms" of Lembra: three
 * category words always visible, the centred one large + bold +
 * navy, the flanking two smaller + faded. The centred word cycles
 * every ~3.5s through {Cakes, Hampers, Flowers, Vouchers}. Each
 * visible word is a clickable link to /browse?category=<slug>, so
 * the row doubles as the brand-promise signifier AND a browse entry
 * point, the "browse" affordance Lucky asked us to keep visible.
 *
 * Why two browse surfaces (cycling row above + inline trailer in the
 * bar): the row is the brand-led entry (visual, exploratory); the
 * inline trailer is the functional fallback (a user staring at the
 * bar still sees "or browse all →"). Both reinforce the implicit-
 * routing principle of the SmartIntentBar, type whatever you want
 * and the system figures out chat vs browse vs nav, but if you'd
 * rather not type, browse is one tap away from any spot in the hero.
 *
 * Pioneer alignment (ux-pioneers-wisdom.md):
 * - Norman: signifiers everywhere, cycling categories signal "we
 *   stock all of these", chips signal "you can type things like
 *   this", typewriter placeholder signals "I'm alive."
 * - Krug: zero forced choice. The user never has to decide between
 *   chat-mode and browse-mode, both are just-in-time visible.
 * - Stanley: cycling row uses real <Link>s with focus rings; the
 *   centre word's animation is opacity-only (no layout shift) so
 *   keyboard users aren't dragged through moving targets.
 */

import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { ArrowRight, Cake, Gift, Sparkles } from "lucide-react";
import { AnimatedReveal } from "../shared/AnimatedReveal";
import { SmartIntentBar } from "@/components/smart-input/SmartIntentBar";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
// MS59: inline-chat surface. When SmartIntentBar classifies a brief
// as `chat`, instead of navigating to /chat we swap the bar out for
// this component and run the full conversation in-place. Honours
// Sprint 1.4's Krishna principle ("chat-vs-browse mode-choice
// collapsed into one input. The system disambiguates instead of
// the user") — current routing defers the mode-choice into a page
// transition; inlining makes the bar genuinely just-disambiguate.
import { InlineConcierge } from "@/components/concierge/InlineConcierge";
import type { InlineConversationBrief } from "@/hooks/useInlineConversation";

// Cycling categories, what the centre word cycles through. Slugs
// match what the /browse page accepts as `?category=`. If the slug
// isn't recognised, /browse falls back to no filter, which is fine.
const CATEGORIES = [
  { label: "Cakes", slug: "cake" },
  { label: "Hampers", slug: "hamper" },
  { label: "Flowers", slug: "flowers" },
  { label: "Vouchers", slug: "voucher" },
];

const CYCLE_MS = 3500;

// Cycling placeholders for the SmartIntentBar, short freeform
// examples that fit every viewport without truncation.
const LANDING_CYCLING_PLACEHOLDERS = [
  "Send Sarah a thoughtful birthday gift…",
  "Browse hampers under $100…",
  "Plan a welcome for our new hire…",
];

const LANDING_SUGGESTIONS = [
  "Send a birthday gift",
  "Browse hampers under $100",
  "Plan a team welcome",
];

// MS13, typed chip variants for the catering-style suggestion row.
// Each chip pairs a suggestion text with a coral Lucide icon. The
// icons map to the rough archetype of each ask (cake = celebration,
// gift = catalogue browse, sparkles = composing a fresh moment).
const LANDING_CHIPS = [
  { label: "Send a birthday gift", icon: Cake },
  { label: "Browse hampers under $100", icon: Gift },
  { label: "Plan a team welcome", icon: Sparkles },
];

/**
 * The cycling-category row. Three slots visible at any time: prev /
 * centre / next. Centre is large + bold + navy + Newsreader italic;
 * prev/next are smaller + faded + same family. Auto-cycles every
 * 3.5s; pauses on hover so a reading user isn't dragged through
 * moving targets. Each word is a real <Link> so keyboard nav and
 * screen readers work normally.
 */
function CyclingCategoryRow() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  // MS23: detect prefers-reduced-motion. When the user has it set,
  // we don't auto-cycle the category words — they stay on the first
  // word and rotate only via direct interaction. Same approach the
  // marquee + StatsSection use.
  //
  // MS184: replaced the inline 8-line reduced-motion plumbing with
  // the shared useReducedMotion hook — same behaviour, one source
  // of truth for the matchMedia subscription.
  const reducedMotion = useReducedMotion();

  const advance = useCallback(() => {
    setActiveIdx((i) => (i + 1) % CATEGORIES.length);
  }, []);

  useEffect(() => {
    if (paused || reducedMotion) return;
    const t = setInterval(advance, CYCLE_MS);
    return () => clearInterval(t);
  }, [paused, reducedMotion, advance]);

  const len = CATEGORIES.length;
  const prev = CATEGORIES[(activeIdx - 1 + len) % len];
  const centre = CATEGORIES[activeIdx];
  const next = CATEGORIES[(activeIdx + 1) % len];

  // Each visible word is rendered as a <Link>. The centre word also
  // gets `key={centre.slug}` so React remounts it on cycle, kicking
  // off a fresh fade-in animation via CSS.
  return (
    <div
      role="navigation"
      aria-label="Browse by category"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "baseline",
        justifyContent: "center",
        gap: "clamp(24px, 4.5vw, 52px)",
        minHeight: "clamp(56px, 7.5vw, 90px)",
      }}
    >
      <Link
        to={`/browse?category=${prev.slug}`}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        className="cycling-cat cycling-cat--ghost"
        style={{
          fontFamily: "Newsreader, Georgia, serif",
          fontStyle: "italic",
          fontWeight: 400,
          fontSize: "clamp(1.3rem, 3vw, 2rem)",
          color: "rgba(29,52,97,0.32)",
          textDecoration: "none",
          letterSpacing: "-0.01em",
          transition: "color 280ms",
        }}
      >
        {prev.label}
      </Link>

      <Link
        key={centre.slug}
        to={`/browse?category=${centre.slug}`}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        className="cycling-cat cycling-cat--centre"
        style={{
          fontFamily: "Newsreader, Georgia, serif",
          fontStyle: "italic",
          fontWeight: 500,
          // S7a: scaled down so it sits beneath the H1 instead of
          // competing with it. Was clamp(2.6rem, 6vw, 4.5rem).
          // Bumped again MS459: +25% on all three clamp values.
          fontSize: "clamp(2.8rem, 6.5vw, 4.8rem)",
          color: "#1D3461",
          textDecoration: "none",
          letterSpacing: "-0.02em",
          lineHeight: 1.05,
          animation: "cyclingFadeIn 700ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {centre.label}
      </Link>

      <Link
        to={`/browse?category=${next.slug}`}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        className="cycling-cat cycling-cat--ghost"
        style={{
          fontFamily: "Newsreader, Georgia, serif",
          fontStyle: "italic",
          fontWeight: 400,
          fontSize: "clamp(1.3rem, 3vw, 2rem)",
          color: "rgba(29,52,97,0.32)",
          textDecoration: "none",
          letterSpacing: "-0.01em",
          transition: "color 280ms",
        }}
      >
        {next.label}
      </Link>

      <style>{`
        .cycling-cat:hover,
        .cycling-cat:focus-visible {
          color: #C4564A !important;
          outline: none;
        }
        .cycling-cat:focus-visible {
          text-decoration: underline !important;
          text-underline-offset: 6px;
          text-decoration-color: #C4564A !important;
        }
        @keyframes cyclingFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export function VariantB() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768,
  );
  // MS491: shorter-laptop carve-out. Lucky's 16" MacBook Pro (1117px
  // viewport height) is the gold standard layout — H1 + cycle row +
  // lede + SmartIntentBar all land comfortably above the fold. On
  // 13" MacBooks (900px) and shorter Windows laptops (768px) the
  // generous top/bottom padding pushed the SmartIntentBar below the
  // fold, making the chat trigger invisible at first paint — Lucky
  // flagged this as the actual conversion-affecting issue (the chat
  // surface stops reading as an obvious modality when you have to
  // scroll to find it).
  //
  // Threshold: 1000px viewport height. Anything below that gets the
  // compact spacing pass — preserves the MBP 16" layout (1117px) and
  // catches every common smaller laptop. Desktop ≥1000px height
  // continues to use the existing roomy spacing.
  const [isShortViewport, setIsShortViewport] = useState(
    () => typeof window !== "undefined" && window.innerHeight < 1000,
  );

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsShortViewport(window.innerHeight < 1000);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Sprint 462 (2026-05-21): chooser-to-celebrations hand-off.
  // When a buyer clicks a footer category on lembra.com.au, that
  // prompt is forwarded here as `?q=…`. We read it ONCE in the
  // initialiser for `chatBrief` below, then strip the param so a
  // refresh / share doesn't keep re-seeding.
  //
  // Earlier version of this (Sprint 462a) only pre-filled the
  // SmartIntentBar. Lucky's feedback: "it just pastes the text into
  // the chat box… feels very gimmicky and clunky." So now we skip
  // the SmartIntentBar entirely when a query is seeded — set
  // chatBrief directly, the hero boots in IN_CHAT mode, and
  // InlineConcierge auto-sends the brief as the first user turn
  // (existing behaviour). Buyer arrives with the conversation
  // already happening, no manual send.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const u = new URL(window.location.href);
    if (!u.searchParams.has("q")) return;
    u.searchParams.delete("q");
    window.history.replaceState(
      window.history.state,
      "",
      u.pathname + (u.search || "") + u.hash,
    );
  }, []);

  // ── MS59 hero state machine ────────────────────────────────────
  //
  //  IDLE:        SmartIntentBar visible, no conversation
  //  IN_CHAT:     InlineConcierge visible, SmartIntentBar hidden;
  //               conversation is live, recommendations + composer
  //               render here. User can "Start over" to return to
  //               IDLE.
  //
  // Browse + nav verdicts still navigate() out for now — inlining
  // the browse picker is MS59.5. Inline ambiguous handling stays
  // inside SmartIntentBar (clarification chips).
  //
  // `chatBrief` carries the verdict + the user's original raw text
  // through to InlineConcierge, which auto-sends it as the first
  // user turn so the model responds immediately on transition (no
  // empty-state moment, the user already typed something).
  //
  // Sprint 462 (2026-05-21): seed from the chooser hand-off too.
  // When the URL carries `?q=…` from lembra.com.au footer click,
  // boot straight into IN_CHAT with that prompt as the brief — no
  // SmartIntentBar flash, no pasted-text-waiting-for-send.
  const [chatBrief, setChatBrief] = useState<InlineConversationBrief | null>(
    () => {
      if (typeof window === "undefined") return null;
      const q = new URLSearchParams(window.location.search).get("q");
      if (q && q.length > 0) {
        return { raw_text: q };
      }
      return null;
    },
  );

  const handleSmartIntentResult = useCallback(
    (
      verdict: {
        intent: "chat" | "browse" | "nav" | "ambiguous";
        recipient_name?: string;
        occasion?: string;
        life_moment?: string;
        vendor_category?: string;
        budget_aud?: number;
        nav_target?: string;
      },
      originalText: string,
    ) => {
      switch (verdict.intent) {
        case "chat": {
          // Transition to IN_CHAT: build the brief and let the
          // InlineConcierge take over. Hero grows to accommodate.
          setChatBrief({
            raw_text: originalText,
            recipient_name: verdict.recipient_name,
            occasion: verdict.occasion,
            life_moment: verdict.life_moment,
            vendor_category: verdict.vendor_category,
            budget_aud: verdict.budget_aud,
          });
          return;
        }

        case "browse": {
          // Browse stays as a page navigation for MS59 — inlining
          // the browse picker into the hero is the MS59.5 follow-up.
          // The escape hatch (cycling categories above + "or browse
          // all" trailer in the bar + nav menu) means the user has
          // multiple visible browse entry points without us having
          // to inline the whole grid yet.
          const params = new URLSearchParams();
          params.set("brief", originalText);
          if (verdict.recipient_name) params.set("recipient", verdict.recipient_name);
          if (verdict.occasion) params.set("occasion", verdict.occasion);
          if (verdict.vendor_category) params.set("category", verdict.vendor_category);
          if (verdict.budget_aud) params.set("budget", String(verdict.budget_aud));
          navigate(`/browse?${params.toString()}`);
          return;
        }

        case "nav": {
          // Nav verdicts always page-navigate. Inlining a "go to
          // settings" intent would be nonsensical — the whole point
          // is to take the user to that page.
          const target = verdict.nav_target;
          if (target === "settings") navigate("/settings");
          else if (target === "team") navigate("/team");
          else if (target === "dashboard") navigate("/dashboard");
          else if (target === "profile" && verdict.recipient_name) {
            navigate(`/team?search=${encodeURIComponent(verdict.recipient_name)}`);
          } else {
            // Unknown nav_target — fall back to chat so the user
            // ends up somewhere sensible rather than nowhere.
            setChatBrief({ raw_text: originalText });
          }
          return;
        }

        // ambiguous never reaches here — SmartIntentBar handles it
        // inline with clarification chips.
      }
    },
    [navigate],
  );

  const resetToIdle = useCallback(() => setChatBrief(null), []);
  const inChat = chatBrief !== null;

  return (
    <section
      style={{
        // MS201: was `100vh`. On iOS Safari `vh` is computed against
        // the viewport WITH the address bar visible, so the hero
        // renders taller than the visible window on first paint and
        // the bottom of the hero sits below the address bar until the
        // user scrolls. `dvh` (dynamic viewport height) tracks the
        // actually-visible viewport and resolves to the correct
        // height immediately. Safari 15.4+, Chrome 108+, Firefox
        // 101+ — supported everywhere we care about.
        minHeight: "100dvh",
        display: "flex",
        // MS-hero-mobile: top-align (cross-axis flex-start in this row flex)
        // once the chat takes over on mobile, so the conversation + composer
        // start just under the nav instead of vertically centred below the
        // (now-hidden) oversized H1 that pushed the widget off-screen.
        // Desktop + idle keep the original centred composition untouched.
        alignItems: inChat && isMobile ? "flex-start" : "center",
        justifyContent: "center",
        // MS11: hero now exits at the SAME FFF8E7 the MultiVendorShowcase
        // enters at, so the two sections read as one continuous wash
        // instead of a hard break. Gradient direction stays 165deg
        // so the diagonal warmth still leads the eye, but the bottom
        // 15% is held in pure FFF8E7 to give MVS's top fade-band
        // something clean to land on.
        background:
          "linear-gradient(180deg, #FFF8E7 0%, #FBF3D0 25%, #F7E8A4 55%, #FBF3D0 78%, #FFF8E7 92%, #FFF8E7 100%)",
        padding: inChat && isMobile
          ? "76px 16px 16px"  // chat mode: clear the fixed nav, hand the rest to the widget
          : isMobile
            ? "104px 20px 56px"
            : isShortViewport
              ? "64px 48px 48px"  // MS491: compact for sub-16"-MBP laptops
              : "120px 48px 88px",
      }}
    >
      <div
        style={{
          maxWidth: "820px",
          width: "100%",
          margin: "0 auto",
          textAlign: "center",
        }}
      >
        {/* MS501: "LEMBRA · WORKPLACE CELEBRATIONS" eyebrow stamp
            removed entirely per Lucky. MS363 had already hidden it
            on mobile (nav already brand-stamps the top); the
            desktop eyebrow was a stale holdover that read as
            redundant ornament above the H1, not editorial
            scaffolding. H1 now opens the hero directly. */}

        {/* ── H1, restored above the cycling row (S7a). Carries the
            brand promise while the cycling row beneath it acts as a
            visual + functional category nav. Workshop the actual line
            later; "Every milestone, /remembered./" is the same
            previously-approved copy.
            MS363: mobile font size bumped from clamp(2rem, 7.5vw,
            2.7rem) = 32–43.2px → clamp(2.6rem, 11vw, 3.4rem) =
            41.6–54.4px so the H1 grows into the vertical space the
            removed eyebrow + lede + subhead freed up (MS362/MS363).
            Reads as the single dominant brand line above the chat
            widget instead of one type block among several. Desktop
            font sizing unchanged. */}
        <AnimatedReveal animation="fadeInUpSoft" duration={800} delay={100}>
          <h1
            style={{
              fontFamily: "Newsreader, Georgia, serif",
              // Mobile: was clamp(2.6rem, 11vw, 3.4rem) = 41-54px.
              // Desktop: was clamp(2.4rem, 4.4vw, 3.6rem) = 38-58px.
              // MS458: bumped ~15-20%. MS459: bumped again +25% per
              // Lucky's "still relatively small" feedback.
              fontSize: isMobile
                ? "clamp(4rem, 15vw, 5.4rem)"
                : "clamp(3.8rem, 7vw, 6rem)",
              lineHeight: 1.03,
              color: "#1D3461",
              fontWeight: 500,
              letterSpacing: "-0.025em",
              margin: isMobile ? "0 0 30px" : isShortViewport ? "0 0 20px" : "0 0 40px",
              // MS-hero-mobile: hide the oversized H1 once the chat owns the
              // mobile viewport. Idle keeps it big + brand-forward (Lucky's
              // MS458/459 call); in-chat the nav already brand-stamps and the
              // conversation needs the vertical room. Desktop is unchanged.
              display: inChat && isMobile ? "none" : undefined,
            }}
          >
            Every milestone,{" "}
            <em
              style={{
                fontStyle: "italic",
                fontWeight: 400,
                color: "#C4564A",
              }}
            >
              remembered.
            </em>
          </h1>
        </AnimatedReveal>

        {/* ── Cycling category row (Heidi-pattern), slightly smaller
            now that the H1 carries the visual top weight ─────────── */}
        {/* MS-hero-mobile: hidden in-chat on mobile (with the H1) so the
            chat widget gets the full viewport. Stays on desktop + idle. */}
        {!(inChat && isMobile) && (
          <AnimatedReveal animation="fadeInUpSoft" duration={750} delay={200}>
            <CyclingCategoryRow />
          </AnimatedReveal>
        )}

        {/* ── Lede ──────────────────────────────────────────────────
            MS59: hidden once we're IN_CHAT. The lede is a sales
            line that helps an idle visitor understand what Lembra
            does; once they've started chatting, it competes with
            the conversation for attention. Keep the H1 + cycling
            categories (they orient + give the browse hatch); lose
            the lede until they reset to idle.
            MS362: also hidden on MOBILE (idle). Per Lucky's call —
            the H1 + cycling categories already carry the brand
            position above the fold; the lede was adding a third
            type-block that the smart-intent bar had to compete
            with. Desktop keeps it: there's enough vertical room
            for the H1 + categories + lede + intent bar to read as
            distinct beats. */}
        {!inChat && !isMobile && (
          <AnimatedReveal animation="fadeInUpSoft" duration={800} delay={280}>
            <p
              style={{
                marginTop: isMobile ? "20px" : isShortViewport ? "14px" : "28px",
                fontSize: isMobile ? "1rem" : "1.125rem",
                lineHeight: 1.55,
                color: "rgba(45,41,38,0.75)",
                fontFamily: "DM Sans, sans-serif",
                maxWidth: "560px",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              A concierge for the celebrations your team would otherwise
              scramble for. Quietly handled. Picked, signed, delivered.
            </p>
          </AnimatedReveal>
        )}

        {/* ── Working surface: SmartIntentBar (IDLE) or InlineConcierge
            (IN_CHAT). Same slot, same column width — the only thing
            that changes is what tool the user is holding right now.
            Honors Krishna ("the system disambiguates, not the user"):
            the user typed one freeform thing, the bar classified it,
            and now they're in the right surface for what they meant —
            no page transition required. */}
        <AnimatedReveal animation="fadeInUp" duration={700} delay={420}>
          <div
            style={{
              marginTop: inChat && isMobile ? "4px" : isMobile ? "32px" : isShortViewport ? "20px" : "44px",
              marginLeft: "auto",
              marginRight: "auto",
              maxWidth: "720px",
              textAlign: "left",
            }}
          >
            {inChat && chatBrief ? (
              <InlineConcierge
                initialBrief={chatBrief}
                onReset={resetToIdle}
              />
            ) : (
              <SmartIntentBar
                cyclingPlaceholders={LANDING_CYCLING_PLACEHOLDERS}
                suggestions={LANDING_SUGGESTIONS}
                landingChips={LANDING_CHIPS}
                landingMode
                /* MS59: hand chat/browse/nav verdicts up to the
                   hero orchestrator instead of letting the bar
                   navigate() us out of the page. The bar still
                   handles ambiguous itself (clarification chips).
                   Note: the chooser-to-celebrations hand-off
                   (Sprint 462) routes around this widget by setting
                   chatBrief on mount, so initialValue isn't needed
                   here anymore. */
                onResultOverride={handleSmartIntentResult}
                landingTrailer={
                  <Link
                    to="/browse"
                    className="hero-browse-trailer text-[13px] text-navy/70 underline decoration-transparent underline-offset-4 transition-colors hover:text-coral hover:decoration-coral"
                    style={{ fontFamily: "DM Sans, sans-serif" }}
                  >
                    or browse all{" "}
                    <ArrowRight
                      size={12}
                      strokeWidth={2.5}
                      style={{ display: "inline", verticalAlign: "-1px" }}
                    />
                  </Link>
                }
              />
            )}
          </div>
        </AnimatedReveal>

        {/* MS-trust: trust line moved OUT of the hero and now renders
            as a standalone centered band between the hero and the
            showcase below (see Index.tsx). It was sitting in an
            awkward middle here — far enough below the chat to feel
            orphaned but not centered between sections. Lifting it
            into its own divider band gives it the breathing room
            it needs and makes it act as a true visual transition. */}
      </div>
    </section>
  );
}
